import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus, Trash2, Search, Printer, FileDown, Mail, Save, X,
    CreditCard, DollarSign, Building2, User, Calendar, Hash,
    Package, Pill, Receipt, AlertCircle, Check, ChevronDown, RotateCcw,
    FlaskConical, Gift
} from 'lucide-react';
import './Billing.css';

import { API_URL } from '../config/api';

// ... (resto del código reemplazando localhost)
// NOTA: Como no puedo hacer un replace global simple de string en este tool si hay lógica compleja alrededor,
// mejor haré un sed/replace en el próximo paso una vez definida la constante.
// Por ahora solo defino la constante correcta si no existe o la corrijo.


// Service types - estudio will load from catalog_studies API
const SERVICE_TYPES = [
    { value: 'consulta', label: 'Consulta' },
    { value: 'estudio', label: 'Estudio (Catálogo)' },
    { value: 'procedimiento', label: 'Procedimiento' },
    { value: 'otro', label: 'Otro' }
];

// Basic services (not studies - those come from catalog)
const SERVICES_CATALOG = {
    consulta: [
        { id: 'c1', name: 'Consulta General', price: 500 },
        { id: 'c2', name: 'Consulta Especialidad', price: 800 },
        { id: 'c3', name: 'Consulta Genética', price: 1500 },
    ],
    procedimiento: [
        { id: 'p1', name: 'Toma de Muestra', price: 200 },
        { id: 'p2', name: 'Biopsia', price: 1500 },
    ],
    otro: [
        { id: 'o1', name: 'Certificado Médico', price: 300 },
        { id: 'o2', name: 'Constancia', price: 150 },
    ],
    estudio: [] // Will be loaded dynamically from catalog_studies
};

const MEDICATIONS_CATALOG = []; // Will be loaded from API


const PAYMENT_METHODS = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta_debito', label: 'Tarjeta de Débito' },
    { value: 'tarjeta_credito', label: 'Tarjeta de Crédito' },
    { value: 'transferencia', label: 'Transferencia Bancaria' },
    { value: 'cheque', label: 'Cheque' },
];

const CFDI_USES = [
    { value: 'G01', label: 'G01 - Adquisición de mercancías' },
    { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
    { value: 'G03', label: 'G03 - Gastos en general' },
    { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
    { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
    { value: 'D03', label: 'D03 - Gastos funerales' },
    { value: 'D04', label: 'D04 - Donativos' },
    { value: 'D05', label: 'D05 - Intereses reales por créditos hipotecarios' },
    { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
    { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
    { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
    { value: 'D09', label: 'D09 - Depósitos en cuentas de ahorro' },
    { value: 'D10', label: 'D10 - Pagos por servicios educativos (colegiaturas)' },
    { value: 'P01', label: 'P01 - Por definir' },
    { value: 'S01', label: 'S01 - Sin efectos fiscales' },
    { value: 'CP01', label: 'CP01 - Pagos' },
    { value: 'CN01', label: 'CN01 - Nómina' }
];

const Billing = ({ initialPatient }) => {
    // Get next sequential folio from cache (will be synced on mount)
    const getNextFolio = () => {
        const lastNumber = parseInt(localStorage.getItem('billing_folio_last') || '0');
        const nextNumber = lastNumber + 1;
        return `R-${nextNumber.toString().padStart(7, '0')}`;
    };

    // Increment folio counter (call this only after successful save)
    const incrementFolio = () => {
        const lastNumber = parseInt(localStorage.getItem('billing_folio_last') || '0');
        localStorage.setItem('billing_folio_last', (lastNumber + 1).toString());
    };

    const [user, setUser] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
    const [searchPatient, setSearchPatient] = useState('');
    const [searchMed, setSearchMed] = useState('');
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [productsFromStorage, setProductsFromStorage] = useState([]);
    const [catalogStudies, setCatalogStudies] = useState([]);

    // Receipt header data
    const getLocalDate = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    };

    const [receipt, setReceipt] = useState({
        patientId: '',
        patientName: '',
        expediente: '',
        doctorId: '',
        fecha: getLocalDate(),
        tipo: 'ingreso',
        moneda: 'MXN',
        descuentoGeneral: 0,
        exento: 0,
        folio: getNextFolio(),
        sucursal: 'GENETICA',
    });

    // Services list
    const [services, setServices] = useState([
        { id: 1, tipo: 'consulta', medicoId: '', servicioId: '', descripcion: '', precio: 0, cantidad: 1, descuento: 0, iva: 16, isr: 0 }
    ]);

    // Medications list
    const [medications, setMedications] = useState([]);

    // Patient Studies (from doctor orders)
    const [patientStudies, setPatientStudies] = useState([]);

    // Fiscal data
    const [fiscal, setFiscal] = useState({
        razonSocial: '',
        rfc: '',
        direccion: '',
        usoCfdi: 'G03',
        formaPago: 'efectivo',
        metodoPago: 'PUE',
        condiciones: 'Pago en una sola exhibición',
        requiresInvoice: false,  // ¿Requiere factura?
        invoiceDelivered: false, // ¿Factura entregada?
    });

    // Payment methods (for mixed payments)
    const [payments, setPayments] = useState([
        { method: 'efectivo', amount: 0 }
    ]);

    // Pre-fill patient data if initialPatient is provided
    useEffect(() => {
        if (initialPatient) {
            const patientName = `${initialPatient.first_name || ''} ${initialPatient.last_name || ''} ${initialPatient.maternal_last_name || ''}`.trim();
            setReceipt(prev => ({
                ...prev,
                patientId: initialPatient.id,
                patientName: patientName,
                expediente: initialPatient.patient_code || initialPatient.id?.toString().padStart(5, '0') || ''
            }));
            setSearchPatient(patientName);
            // Fetch patient studies when coming from patient view
            fetchPatientStudies(initialPatient.id);
        }
    }, [initialPatient]);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                setUser(JSON.parse(userData));
            } catch (e) {
                console.error('Error parsing user:', e);
            }
        }
        fetchDoctors();
        fetchPatients();
        fetchProducts();
        fetchCatalogStudies();
        syncFolio();
    }, []);

    const syncFolio = async () => {
        try {
            const token = localStorage.getItem('token');
            // Use the new endpoint to get next folio
            const res = await fetch(`${API_URL}/receipts/next-folio`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data && data.folio) {
                    // data.folio is in format R-0000001
                    // data.sequence is the number (e.g., 1)
                    const lastNumber = data.sequence - 1; // The last saved number
                    localStorage.setItem('billing_folio_last', lastNumber.toString());
                    setReceipt(prev => ({
                        ...prev,
                        folio: data.folio
                    }));
                }
            } else {
                // Fallback to R-0000001 on error
                localStorage.setItem('billing_folio_last', '0');
                setReceipt(prev => ({
                    ...prev,
                    folio: 'R-0000001'
                }));
            }
        } catch (e) {
            console.error('Error syncing folio:', e);
            // Fallback on network error
            setReceipt(prev => ({
                ...prev,
                folio: getNextFolio()
            }));
        }
    };

    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/doctors`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDoctors(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error('Error fetching doctors:', e);
            setDoctors([
                { id: 2, name: 'Marisol' },
                { id: 3, name: 'Joel' }
            ]);
        }
    };

    const fetchProducts = async () => {
        try {
            const token = localStorage.getItem('token');
            // First get categories to find medicamento category
            const catRes = await fetch(`${API_URL}/categories`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            let medicamentoCategoryId = null;
            if (catRes.ok) {
                const categories = await catRes.json();
                const medCat = categories.find(c =>
                    c.name.toLowerCase().includes('medicamento') ||
                    c.name.toLowerCase().includes('farmacia') ||
                    c.name.toLowerCase().includes('medicina')
                );
                if (medCat) medicamentoCategoryId = medCat.id;
            }

            // Fetch products, optionally filtered by category
            const url = medicamentoCategoryId
                ? `${API_URL}/products?category_id=${medicamentoCategoryId}`
                : `${API_URL}/products`;
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                // Transform products to medication format
                const meds = (Array.isArray(data) ? data : []).map(p => ({
                    code: `PROD-${p.id}`,
                    id: p.id,
                    name: p.name,
                    price: parseFloat(p.sale_price) || parseFloat(p.purchase_price) || 0,
                    stock: parseInt(p.current_stock) || 0,
                    category: p.category_name || ''
                }));
                setProductsFromStorage(meds);
            }
        } catch (e) {
            console.error('Error fetching products:', e);
        }
    };

    const fetchCatalogStudies = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/catalog-studies`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const studies = (Array.isArray(data) ? data : []).map(s => ({
                    id: `study_${s.id}`,
                    name: s.name,
                    price: parseFloat(s.price) || 0,
                    category: s.category || '',
                    code: s.code || ''
                }));
                setCatalogStudies(studies);
            }
        } catch (e) {
            console.error('Error fetching catalog studies:', e);
        }
    };

    const fetchPatients = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patients?per_page=100`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPatients(data.data || data || []);
            }
        } catch (e) {
            console.error('Error fetching patients:', e);
        }
    };

    const filteredPatients = useMemo(() => {
        if (!searchPatient) return [];
        const term = searchPatient.toLowerCase();
        return patients.filter(p =>
            `${p.first_name} ${p.last_name}${p.maternal_last_name ? ' ' + p.maternal_last_name : ''}`.toLowerCase().includes(term) ||
            p.patient_code?.toLowerCase().includes(term)
        ).slice(0, 10);
    }, [patients, searchPatient]);

    const filteredMedications = useMemo(() => {
        if (!searchMed) return productsFromStorage;
        const term = searchMed.toLowerCase();
        return productsFromStorage.filter(m =>
            m.name.toLowerCase().includes(term) || m.code.toLowerCase().includes(term)
        );
    }, [searchMed, productsFromStorage]);

    // Fetch pending studies for a patient (only unbilled ones)
    const fetchPatientStudies = async (patientId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patients/${patientId}/studies`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                // Filter only non-cancelled AND non-billed studies
                const pendingStudies = data.filter(s =>
                    s.status !== 'cancelado' && !s.is_billed
                );
                setPatientStudies(pendingStudies);
            }
        } catch (e) {
            console.error('Error fetching patient studies:', e);
        }
    };

    const selectPatient = (patient) => {
        // Update receipt with patient info
        setReceipt(prev => ({
            ...prev,
            patientId: patient.id,
            patientName: `${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`,
            expediente: patient.patient_code || patient.id.toString().padStart(5, '0')
        }));
        setSearchPatient(`${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`);
        setShowPatientDropdown(false);

        // Auto-fill fiscal data from patient's saved fiscal info
        // Use saved fiscal data if available, otherwise construct from name/address
        const fullName = `${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`;
        const addressParts = [
            patient.address_street,
            patient.address_number ? `#${patient.address_number}` : '',
            patient.address_colony ? `Col. ${patient.address_colony}` : '',
            patient.address_city,
            patient.address_state,
            patient.address_zip ? `CP ${patient.address_zip}` : ''
        ].filter(Boolean);

        setFiscal(prev => ({
            ...prev,
            // Solo usar datos fiscales guardados, no rellenar con datos personales
            razonSocial: patient.fiscal_razon_social || patient.fiscal_name || '',
            rfc: patient.fiscal_rfc || '',
            direccion: patient.fiscal_direccion || '',
            usoCfdi: patient.fiscal_uso_cfdi || patient.fiscal_cfdi_use || 'G03',
        }));

        // Fetch patient studies for billing
        fetchPatientStudies(patient.id);
    };

    // Service functions
    const addService = () => {
        setServices(prev => [...prev, {
            id: Date.now(),
            tipo: 'consulta',
            medicoId: '',
            servicioId: '',
            descripcion: '',
            precio: 0,
            cantidad: 1,
            descuento: 0,
            iva: 16,
            isr: 0
        }]);
    };

    const updateService = (id, field, value) => {
        setServices(prev => prev.map(s => {
            if (s.id !== id) return s;
            const updated = { ...s, [field]: value };

            // Auto-fill price when service is selected
            if (field === 'servicioId' && value) {
                const catalog = SERVICES_CATALOG[s.tipo] || [];
                const service = catalog.find(svc => svc.id === parseInt(value));
                if (service) {
                    updated.precio = service.price;
                    updated.descripcion = service.name;
                }
            }

            // Reset service when type changes
            if (field === 'tipo') {
                updated.servicioId = '';
                updated.precio = 0;
                updated.descripcion = '';
            }

            return updated;
        }));
    };

    const removeService = (id) => {
        if (services.length > 1) {
            setServices(prev => prev.filter(s => s.id !== id));
        }
    };

    // Medication functions
    const addMedication = (med) => {
        // Check stock availability
        if (med.stock <= 0) {
            setError(`¡ALERTA! El producto "${med.name}" no tiene stock disponible.`);
            return;
        }

        const existing = medications.find(m => m.code === med.code);
        if (existing) {
            // Check if adding more exceeds stock
            if (existing.cantidad + 1 > med.stock) {
                setError(`¡ALERTA! Solo hay ${med.stock} unidades disponibles de "${med.name}".`);
                return;
            }
            setMedications(prev => prev.map(m =>
                m.code === med.code ? { ...m, cantidad: m.cantidad + 1 } : m
            ));
        } else {
            setMedications(prev => [...prev, { ...med, cantidad: 1, descuento: 0 }]);
        }
        setSearchMed('');
        setError(''); // Clear any previous error
    };

    const updateMedication = (code, field, value) => {
        setMedications(prev => prev.map(m =>
            m.code === code ? { ...m, [field]: value } : m
        ));
    };

    const removeMedication = (code) => {
        setMedications(prev => prev.filter(m => m.code !== code));
    };

    // Payment functions
    const addPayment = () => {
        setPayments(prev => [...prev, { method: 'efectivo', amount: 0 }]);
    };

    const updatePayment = (index, field, value) => {
        setPayments(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };

    const removePayment = (index) => {
        if (payments.length > 1) {
            setPayments(prev => prev.filter((_, i) => i !== index));
        }
    };

    // Calculations
    const calculations = useMemo(() => {
        let subtotalServicios = 0;
        let ivaServicios = 0;
        let isrServicios = 0;

        services.forEach(s => {
            const subtotal = s.precio * s.cantidad;
            const descuento = subtotal * (s.descuento / 100);
            const base = subtotal - descuento;
            subtotalServicios += base;
            ivaServicios += base * (s.iva / 100);
            isrServicios += base * (s.isr / 100);
        });

        let subtotalMedicamentos = 0;
        let ivaMedicamentos = 0;

        medications.forEach(m => {
            const subtotal = m.price * m.cantidad;
            const descuento = subtotal * ((m.descuento || 0) / 100);
            const base = subtotal - descuento;
            subtotalMedicamentos += base;
            ivaMedicamentos += base * 0.16; // 16% IVA for medications
        });

        const subtotalGeneral = subtotalServicios + subtotalMedicamentos;
        const descuentoGeneral = subtotalGeneral * (receipt.descuentoGeneral / 100);
        const exento = subtotalGeneral * (receipt.exento / 100);
        const totalIva = ivaServicios + ivaMedicamentos;
        const totalIsr = isrServicios;
        const totalGeneral = subtotalGeneral - descuentoGeneral - exento + totalIva - totalIsr;
        const totalPagado = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        const pendiente = totalGeneral - totalPagado;

        return {
            subtotalServicios,
            subtotalMedicamentos,
            subtotalGeneral,
            descuentoGeneral,
            exento,
            totalIva,
            totalIsr,
            totalGeneral,
            totalPagado,
            pendiente
        };
    }, [services, medications, receipt.descuentoGeneral, receipt.exento, payments]);

    const handleSave = async () => {
        if (saving) return;
        // Validations - require at least one item (service, medication, or study)
        if (services.every(s => !s.servicioId) && medications.length === 0 && patientStudies.length === 0) {
            setError('Agregue al menos un servicio, medicamento o estudio');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const token = localStorage.getItem('token');

            // Prepare studies for billing
            const studiesToBill = patientStudies.map(s => ({
                id: s.id,
                study_name: s.study_name,
                price: s.is_cost_exempt ? 0 : parseFloat(s.price || 0),
                is_cost_exempt: s.is_cost_exempt
            }));

            const payload = {
                patientId: receipt.patientId,
                patientName: receipt.patientName,
                expediente: receipt.expediente,
                doctorId: receipt.doctorId,
                doctorName: doctors.find(d => d.id == receipt.doctorId)?.name,
                tipo: receipt.tipo,
                sucursal: receipt.sucursal,
                moneda: receipt.moneda,
                items: [
                    ...services.filter(s => s.servicioId || s.descripcion).map(s => ({ ...s, category: 'service' })),
                    ...medications.map(m => ({ ...m, category: 'medication' })),
                    ...studiesToBill.map(s => ({ ...s, category: 'study' }))
                ],
                services: services.filter(s => s.servicioId || s.descripcion),
                medications: medications,
                patientStudyIds: patientStudies.map(s => s.id), // IDs to mark as billed
                payments: payments.reduce((acc, p) => ({ ...acc, [p.method]: parseFloat(p.amount) }), {}),
                totals: calculations,
                userId: user?.id,
                userName: user?.name
            };

            const res = await fetch(`${API_URL}/receipts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.message || 'Error al guardar el recibo');
            }

            const data = await res.json();

            // Mark studies as billed
            if (patientStudies.length > 0) {
                await markStudiesAsBilled(patientStudies.map(s => s.id), data.id);
            }

            // Success
            setSuccess(`Recibo ${data.folio} guardado correctamente`);

            // Increment local counter
            incrementFolio();

            setTimeout(() => {
                setSuccess('');
                handleReset();
            }, 2000);

        } catch (e) {
            console.error(e);
            setError(e.message || 'Error al guardar el recibo');
        } finally {
            setSaving(false);
        }
    };

    // Mark patient studies as billed
    const markStudiesAsBilled = async (studyIds, receiptId) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/patient-studies/mark-billed`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ study_ids: studyIds, receipt_id: receiptId })
            });
        } catch (e) {
            console.error('Error marking studies as billed:', e);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleReset = () => {
        // Reset receipt header
        setReceipt({
            patientId: '',
            patientName: '',
            expediente: '',
            doctorId: '',
            fecha: getLocalDate(),
            tipo: 'ingreso',
            moneda: 'MXN',
            descuentoGeneral: 0,
            exento: 0,
            folio: getNextFolio(),
            sucursal: 'GENETICA',
        });
        setSearchPatient('');
        // Reset services
        setServices([
            { id: 1, tipo: 'consulta', medicoId: '', servicioId: '', descripcion: '', precio: 0, cantidad: 1, descuento: 0, iva: 16, isr: 0 }
        ]);
        // Reset medications
        setMedications([]);
        // Reset patient studies
        setPatientStudies([]);
        // Reset fiscal data
        setFiscal({
            razonSocial: '',
            rfc: '',
            direccion: '',
            usoCfdi: 'G03',
            formaPago: 'efectivo',
            metodoPago: 'PUE',
            condiciones: 'Pago en una sola exhibición',
            requiresInvoice: false,
            invoiceDelivered: false,
        });
        // Reset payments
        setPayments([{ method: 'efectivo', amount: 0 }]);
        // Clear messages
        setError('');
        setSuccess('');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: receipt.moneda }).format(amount);
    };

    return (
        <div className="billing-container">
            {/* Header */}
            <div className="billing-header">
                <div className="billing-title-wrapper">
                    <div className="billing-title">
                        <Receipt size={28} />
                        <h2>Módulo de Cobranza</h2>
                    </div>
                    <p>Gestión de recibos y facturación clínica</p>
                </div>
                <div className="billing-folio">
                    <span className="folio-label">Folio:</span>
                    <span className="folio-number">{receipt.folio}</span>
                </div>
            </div>

            {/* Alerts */}
            {error && <div className="billing-alert billing-alert-error"><AlertCircle size={18} /> {error}</div>}
            {success && <div className="billing-alert billing-alert-success"><Check size={18} /> {success}</div>}

            <div className="billing-content">
                {/* Left Column - Forms */}
                <div className="billing-main">
                    {/* Receipt Header Card */}
                    <div className="billing-card">
                        <h3><Hash size={18} /> Datos del Recibo</h3>
                        <div className="billing-grid">
                            <div className="form-group">
                                <label>Paciente *</label>
                                <div className="autocomplete-wrapper">
                                    <input
                                        type="text"
                                        placeholder="Buscar paciente..."
                                        value={searchPatient}
                                        onChange={(e) => { setSearchPatient(e.target.value); setShowPatientDropdown(true); }}
                                        onFocus={() => setShowPatientDropdown(true)}
                                    />
                                    {showPatientDropdown && filteredPatients.length > 0 && (
                                        <div className="autocomplete-dropdown">
                                            {filteredPatients.map(p => (
                                                <div key={p.id} className="autocomplete-item" onClick={() => selectPatient(p)}>
                                                    <User size={14} />
                                                    <span>{p.first_name} {p.last_name} {p.maternal_last_name || ''}</span>
                                                    <small>{p.patient_code ? `Exp: ${p.patient_code}` : `ID: ${p.id}`}</small>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>No. Expediente</label>
                                <input type="text" value={receipt.expediente} readOnly className="readonly" />
                            </div>
                            <div className="form-group">
                                <label>Médico Responsable *</label>
                                <select value={receipt.doctorId} onChange={(e) => setReceipt(prev => ({ ...prev, doctorId: e.target.value }))}>
                                    <option value="">Seleccionar...</option>
                                    <option value="NA">N/A - No Aplica</option>
                                    {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Fecha</label>
                                <input type="date" value={receipt.fecha} onChange={(e) => setReceipt(prev => ({ ...prev, fecha: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label>Tipo de Recibo</label>
                                <select value={receipt.tipo} onChange={(e) => setReceipt(prev => ({ ...prev, tipo: e.target.value }))}>
                                    <option value="ingreso">Ingreso</option>
                                    <option value="egreso">Egreso</option>
                                    <option value="nota_credito">Nota de Crédito</option>
                                    <option value="cancelacion">Cancelación</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Moneda</label>
                                <select value={receipt.moneda} onChange={(e) => setReceipt(prev => ({ ...prev, moneda: e.target.value }))}>
                                    <option value="MXN">MXN - Peso Mexicano</option>
                                    <option value="USD">USD - Dólar</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Sucursal</label>
                                <select value={receipt.sucursal} onChange={(e) => setReceipt(prev => ({ ...prev, sucursal: e.target.value }))}>
                                    <option value="GENETICA">Genética</option>
                                    <option value="PRINCIPAL">Principal</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>% Descuento General</label>
                                <input type="number" min="0" max="100" value={receipt.descuentoGeneral} onChange={(e) => setReceipt(prev => ({ ...prev, descuentoGeneral: parseFloat(e.target.value) || 0 }))} />
                            </div>
                        </div>
                    </div>

                    {/* Services Card */}
                    <div className="billing-card">
                        <div className="card-header-flex">
                            <h3><Package size={18} /> Servicios / Estudios / Consultas</h3>
                            <button className="btn-add" onClick={addService}><Plus size={16} /> Agregar Línea</button>
                        </div>
                        <div className="table-responsive">
                            <table className="billing-table">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Médico</th>
                                        <th>Servicio</th>
                                        <th>Descripción</th>
                                        <th>Precio</th>
                                        <th>Cant.</th>
                                        <th>Desc.%</th>
                                        <th>IVA%</th>
                                        <th>ISR%</th>
                                        <th>Subtotal</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {services.map(s => {
                                        const subtotal = s.precio * s.cantidad;
                                        const descuento = subtotal * (s.descuento / 100);
                                        const iva = (subtotal - descuento) * (s.iva / 100);
                                        const isr = (subtotal - descuento) * (s.isr / 100);
                                        const total = subtotal - descuento + iva - isr;

                                        return (
                                            <tr key={s.id}>
                                                <td>
                                                    <select value={s.tipo} onChange={(e) => updateService(s.id, 'tipo', e.target.value)}>
                                                        {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select value={s.medicoId} onChange={(e) => updateService(s.id, 'medicoId', e.target.value)}>
                                                        <option value="">-</option>
                                                        {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                    </select>
                                                </td>
                                                <td className="service-select-cell">
                                                    <select
                                                        value={s.servicioId}
                                                        onChange={(e) => {
                                                            const selectedId = e.target.value;
                                                            updateService(s.id, 'servicioId', selectedId);
                                                            // Auto-fill price when selecting a service
                                                            const servicesList = s.tipo === 'estudio' ? catalogStudies : (SERVICES_CATALOG[s.tipo] || []);
                                                            const selected = servicesList.find(svc => svc.id == selectedId || svc.id === selectedId);
                                                            if (selected) {
                                                                updateService(s.id, 'precio', selected.price);
                                                                updateService(s.id, 'descripcion', selected.name);
                                                            }
                                                        }}
                                                        className="service-select-wide"
                                                    >
                                                        <option value="">Seleccionar...</option>
                                                        {(s.tipo === 'estudio' ? catalogStudies : (SERVICES_CATALOG[s.tipo] || [])).map(svc => (
                                                            <option key={svc.id} value={svc.id}>
                                                                {svc.name} - ${svc.price?.toFixed(2)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td><input type="text" value={s.descripcion} onChange={(e) => updateService(s.id, 'descripcion', e.target.value)} placeholder="Descripción..." /></td>
                                                <td><input type="number" className="input-number" value={s.precio} onChange={(e) => updateService(s.id, 'precio', parseFloat(e.target.value) || 0)} /></td>
                                                <td><input type="number" className="input-number input-sm" min="1" value={s.cantidad} onChange={(e) => updateService(s.id, 'cantidad', parseInt(e.target.value) || 1)} /></td>
                                                <td><input type="number" className="input-number input-sm" min="0" max="100" value={s.descuento} onChange={(e) => updateService(s.id, 'descuento', parseFloat(e.target.value) || 0)} /></td>
                                                <td><input type="number" className="input-number input-sm" value={s.iva} onChange={(e) => updateService(s.id, 'iva', parseFloat(e.target.value) || 0)} /></td>
                                                <td><input type="number" className="input-number input-sm" value={s.isr} onChange={(e) => updateService(s.id, 'isr', parseFloat(e.target.value) || 0)} /></td>
                                                <td className="text-right"><strong>{formatCurrency(total)}</strong></td>
                                                <td><button className="btn-delete" onClick={() => removeService(s.id)}><Trash2 size={14} /></button></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Patient Studies Card - Studies ordered by doctors */}
                    {patientStudies.length > 0 && (
                        <div className="billing-card studies-card">
                            <div className="card-header-flex">
                                <h3><FlaskConical size={18} /> Estudios Solicitados por el Médico</h3>
                                <span className="study-count-badge">{patientStudies.length} estudio(s)</span>
                            </div>
                            <div className="patient-studies-list">
                                {patientStudies.map(study => {
                                    const price = parseFloat(study.price || 0);
                                    const finalPrice = study.is_cost_exempt ? 0 : price;

                                    return (
                                        <div key={study.id} className={`patient-study-item ${study.is_cost_exempt ? 'exempt' : ''}`}>
                                            <div className="study-info">
                                                <span className="study-name">{study.study_name}</span>
                                                <span className="study-meta">
                                                    {study.category} • Médico: {study.doctor_name}
                                                </span>
                                            </div>
                                            <div className="study-status">
                                                <span className={`status-badge status-${study.status}`}>
                                                    {study.status === 'solicitado' && 'Solicitado'}
                                                    {study.status === 'pendiente_muestra' && 'Pendiente Muestra'}
                                                    {study.status === 'muestra_tomada' && 'Muestra Tomada'}
                                                    {study.status === 'en_laboratorio' && 'En Laboratorio'}
                                                    {study.status === 'finalizado' && 'Finalizado'}
                                                </span>
                                            </div>
                                            <div className="study-price-col">
                                                {study.is_cost_exempt ? (
                                                    <div className="exempt-badge">
                                                        <Gift size={14} />
                                                        <span>Exento</span>
                                                    </div>
                                                ) : null}
                                                <span className={`study-price ${study.is_cost_exempt ? 'crossed' : ''}`}>
                                                    {formatCurrency(price)}
                                                </span>
                                                {study.is_cost_exempt && (
                                                    <span className="study-final-price">{formatCurrency(0)}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="studies-total">
                                <span>Total Estudios:</span>
                                <strong>
                                    {formatCurrency(
                                        patientStudies.reduce((sum, s) =>
                                            sum + (s.is_cost_exempt ? 0 : parseFloat(s.price || 0)), 0
                                        )
                                    )}
                                </strong>
                            </div>
                        </div>
                    )}

                    {/* Medications Card */}
                    <div className="billing-card">
                        <div className="card-header-flex">
                            <h3><Pill size={18} /> Medicamentos</h3>
                        </div>
                        <div className="med-search">
                            <input
                                type="text"
                                placeholder="Buscar medicamento por nombre o código..."
                                value={searchMed}
                                onChange={(e) => setSearchMed(e.target.value)}
                            />
                        </div>
                        {searchMed && (
                            <div className="med-suggestions">
                                {filteredMedications.map(m => (
                                    <div key={m.code} className="med-suggestion" onClick={() => addMedication(m)}>
                                        <span className="med-code">{m.code}</span>
                                        <span className="med-name">{m.name}</span>
                                        <span className="med-price">{formatCurrency(m.price)}</span>
                                        <span className="med-stock">Stock: {m.stock}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {medications.length > 0 && (
                            <div className="table-responsive">
                                <table className="billing-table">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Nombre</th>
                                            <th>Precio</th>
                                            <th>Cantidad</th>
                                            <th>Desc.%</th>
                                            <th>IVA</th>
                                            <th>Total</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {medications.map(m => {
                                            const subtotal = m.price * m.cantidad;
                                            const descuento = subtotal * ((m.descuento || 0) / 100);
                                            const iva = (subtotal - descuento) * 0.16;
                                            const total = subtotal - descuento + iva;

                                            return (
                                                <tr key={m.code}>
                                                    <td><span className="badge-code">{m.code}</span></td>
                                                    <td>{m.name}</td>
                                                    <td>{formatCurrency(m.price)}</td>
                                                    <td><input type="number" className="input-number input-sm" min="1" max={m.stock} value={m.cantidad} onChange={(e) => updateMedication(m.code, 'cantidad', parseInt(e.target.value) || 1)} /></td>
                                                    <td><input type="number" className="input-number input-sm" min="0" max="100" value={m.descuento || 0} onChange={(e) => updateMedication(m.code, 'descuento', parseFloat(e.target.value) || 0)} /></td>
                                                    <td>{formatCurrency(iva)}</td>
                                                    <td className="text-right"><strong>{formatCurrency(total)}</strong></td>
                                                    <td><button className="btn-delete" onClick={() => removeMedication(m.code)}><Trash2 size={14} /></button></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {medications.length === 0 && <p className="empty-message">No hay medicamentos agregados. Use el buscador para agregar.</p>}
                    </div>

                    {/* Fiscal Data Card */}
                    <div className="billing-card">
                        <h3><Building2 size={18} /> Facturación</h3>

                        {/* Invoice Checkboxes - Destacados */}
                        <div className="invoice-checkboxes">
                            <label className={`invoice-checkbox ${fiscal.requiresInvoice ? 'checked' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={fiscal.requiresInvoice}
                                    onChange={(e) => setFiscal(prev => ({ ...prev, requiresInvoice: e.target.checked }))}
                                />
                                <span className="checkbox-icon">{fiscal.requiresInvoice ? '✓' : ''}</span>
                                <span className="checkbox-label">¿Requiere Factura?</span>
                            </label>

                            <label className={`invoice-checkbox ${fiscal.invoiceDelivered ? 'checked delivered' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={fiscal.invoiceDelivered}
                                    onChange={(e) => setFiscal(prev => ({ ...prev, invoiceDelivered: e.target.checked }))}
                                    disabled={!fiscal.requiresInvoice}
                                />
                                <span className="checkbox-icon">{fiscal.invoiceDelivered ? '✓' : ''}</span>
                                <span className="checkbox-label">Factura Entregada</span>
                            </label>
                        </div>

                        {/* Mostrar datos fiscales solo si requiere factura */}
                        {fiscal.requiresInvoice && (
                            <div className="billing-grid fiscal-details">
                                <div className="form-group span-2">
                                    <label>Razón Social</label>
                                    <input type="text" value={fiscal.razonSocial} onChange={(e) => setFiscal(prev => ({ ...prev, razonSocial: e.target.value }))} placeholder="Nombre o razón social del cliente" />
                                </div>
                                <div className="form-group">
                                    <label>RFC</label>
                                    <input type="text" value={fiscal.rfc} onChange={(e) => setFiscal(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))} placeholder="RFC del cliente" maxLength={13} />
                                </div>
                                <div className="form-group span-3">
                                    <label>Dirección Fiscal</label>
                                    <input type="text" value={fiscal.direccion} onChange={(e) => setFiscal(prev => ({ ...prev, direccion: e.target.value }))} placeholder="Calle, número, colonia, CP, ciudad" />
                                </div>
                                <div className="form-group">
                                    <label>Uso CFDI</label>
                                    <select value={fiscal.usoCfdi} onChange={(e) => setFiscal(prev => ({ ...prev, usoCfdi: e.target.value }))}>
                                        {CFDI_USES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Método de Pago</label>
                                    <select value={fiscal.metodoPago} onChange={(e) => setFiscal(prev => ({ ...prev, metodoPago: e.target.value }))}>
                                        <option value="PUE">PUE - Pago en una exhibición</option>
                                        <option value="PPD">PPD - Pago en parcialidades</option>
                                    </select>
                                </div>
                                <div className="form-group span-2">
                                    <label>Condiciones de Pago</label>
                                    <input type="text" value={fiscal.condiciones} onChange={(e) => setFiscal(prev => ({ ...prev, condiciones: e.target.value }))} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Payment Methods Card */}
                    <div className="billing-card">
                        <div className="card-header-flex">
                            <h3><CreditCard size={18} /> Formas de Pago</h3>
                            <button className="btn-add" onClick={addPayment}><Plus size={16} /> Agregar Pago</button>
                        </div>
                        <div className="payments-grid">
                            {payments.map((p, i) => (
                                <div key={i} className="payment-row">
                                    <select value={p.method} onChange={(e) => updatePayment(i, 'method', e.target.value)}>
                                        {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                    <div className="payment-amount">
                                        <DollarSign size={16} />
                                        <input
                                            type="number"
                                            value={p.amount}
                                            onChange={(e) => updatePayment(i, 'amount', e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    {payments.length > 1 && (
                                        <button className="btn-delete" onClick={() => removePayment(i)}><Trash2 size={14} /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="payment-summary">
                            <span>Total a pagar: <strong>{formatCurrency(calculations.totalGeneral)}</strong></span>
                            <span>Total pagado: <strong>{formatCurrency(calculations.totalPagado)}</strong></span>
                            <span className={calculations.pendiente > 0 ? 'pending' : 'paid'}>
                                Pendiente: <strong>{formatCurrency(calculations.pendiente)}</strong>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right Column - Totals Sidebar */}
                <div className="billing-sidebar">
                    <div className="totals-card">
                        <h3>Resumen del Recibo</h3>

                        <div className="totals-section">
                            <h4>Servicios</h4>
                            <div className="total-row">
                                <span>Subtotal Servicios</span>
                                <span>{formatCurrency(calculations.subtotalServicios)}</span>
                            </div>
                        </div>

                        <div className="totals-section">
                            <h4>Medicamentos</h4>
                            <div className="total-row">
                                <span>Subtotal Medicamentos</span>
                                <span>{formatCurrency(calculations.subtotalMedicamentos)}</span>
                            </div>
                        </div>

                        <div className="totals-divider"></div>

                        <div className="total-row">
                            <span>Subtotal General</span>
                            <span>{formatCurrency(calculations.subtotalGeneral)}</span>
                        </div>
                        <div className="total-row discount">
                            <span>Descuento General ({receipt.descuentoGeneral}%)</span>
                            <span>-{formatCurrency(calculations.descuentoGeneral)}</span>
                        </div>
                        <div className="total-row">
                            <span>IVA</span>
                            <span>+{formatCurrency(calculations.totalIva)}</span>
                        </div>
                        {calculations.totalIsr > 0 && (
                            <div className="total-row retention">
                                <span>ISR Retenido</span>
                                <span>-{formatCurrency(calculations.totalIsr)}</span>
                            </div>
                        )}

                        <div className="totals-divider"></div>

                        <div className="total-row total-final">
                            <span>TOTAL A PAGAR</span>
                            <span>{formatCurrency(calculations.totalGeneral)}</span>
                        </div>

                        {calculations.pendiente > 0 && calculations.totalPagado > 0 && (
                            <div className="total-row pending-row">
                                <span>Pendiente</span>
                                <span>{formatCurrency(calculations.pendiente)}</span>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="actions-card">
                        <button className="btn-action btn-reset" onClick={handleReset}>
                            <RotateCcw size={18} />
                            Nuevo Recibo
                        </button>
                        <button className="btn-action btn-primary" onClick={handleSave} disabled={saving}>
                            <Save size={18} />
                            {saving ? 'Guardando...' : 'Guardar Recibo'}
                        </button>
                        <button className="btn-action btn-secondary" onClick={handlePrint}>
                            <Printer size={18} />
                            Imprimir
                        </button>
                        <button className="btn-action btn-secondary">
                            <FileDown size={18} />
                            Descargar PDF
                        </button>
                        <button className="btn-action btn-secondary">
                            <Mail size={18} />
                            Enviar por Email
                        </button>
                        <button className="btn-action btn-danger">
                            <X size={18} />
                            Cancelar Recibo
                        </button>
                    </div>

                    {/* Cashier Info */}
                    <div className="cashier-info">
                        <User size={14} />
                        <span>Responsable: <strong>{user?.name || 'Usuario'}</strong></span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Billing;
